/**
 * Hit resolution: weapon + modules + attacker stats vs. what the defender is
 * made of.
 *
 * The rule that shapes this whole file is docs/03 §6: **there is no per-enemy
 * weakness table.** Effectiveness is computed from the defender's material
 * composition by reading the same `M.*` arrays the cell simulation reads. A
 * steel knight resists fire because steel has a high heat capacity, not because
 * someone typed `fireResist: 0.6` next to the knight. The consequence is that
 * adding a material automatically gives every weapon a correct opinion about it,
 * and that an enemy can never be authored outside the systems.
 *
 * The second core mechanic here is primer detonation (docs/04 §1). A primer
 * lives on the target *or on the terrain* — oil you sprayed on the floor is a
 * primer — and an off-hand that detonates it gets an amplified, transformed
 * effect rather than a flat damage bonus.
 */

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import { BodyFlag, Comp, DAMAGE_TYPE_NAMES, DamageType, World } from '../entity/ecs';
import type { DamageTypeId } from '../entity/ecs';
import { Category, Flag, M, Tag, VOID } from '../sim/materials';
import type { MatId } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';
import {
  applyStatus, brittleMul, damageTakenMul, hasStatus, removeStatus,
  shatterFrozen, statusStacks,
} from './status';
import {
  attackerHitstopMs, chargeKindOf, initialCharge, knockbackScale, magazineOf,
  targetHitstopMs,
} from './weaponData';
import {
  LOW_HEALTH_FRAC, PrimerKind, StatusKind, TRIGGER_FLAG_OF, TriggerFlag,
} from './types';
import type {
  ComboNode, CombatStats, Hand, ModuleDef, ModuleEffect, PrimerKindId,
  ResolvedWeapon, StatusApplication, TerrainInteraction, TriggerMask, WeaponDef,
  WeaponInstance,
} from './types';

// ================================================== module folding

function emptyTerrain(): TerrainInteraction {
  return { destroy: { maxHardness: 0, radius: 0, chance: 0 }, displaces: false };
}

/**
 * Fold a weapon definition and its installed modules into the flat struct the
 * hit path reads. Called on equip and on module change — never per frame.
 */
export function resolveWeapon(
  def: WeaponDef,
  instance: WeaponInstance,
  modules: ReadonlyMap<string, ModuleDef>,
): ResolvedWeapon {
  // Rack the instance the first time anyone folds it. `rollWeapon` has the
  // definition *id* and not the definition, so the magazine size and the
  // starting charge — full for a drill's fuel tank, empty for everything else —
  // can only be decided here, and `-1` is what says "not yet decided". A weapon
  // that ran itself dry sits at 0 and is not re-racked, so swapping hands is
  // not a free reload.
  if (instance.ammo < 0) instance.ammo = magazineOf(def);
  if (instance.charge < 0) instance.charge = initialCharge(def);

  const r: ResolvedWeapon = {
    def,
    instance,
    damage: def.damage * instance.rolled.damageMul,
    damageType: def.damageType,
    weight: clamp(def.weight + instance.rolled.weightAdd, 0.05, 1),
    reach: def.reach * instance.rolled.reachMul,
    // The product of the whole `cadenceMul` chain. Nothing on the swing path
    // reads it on its own — `cadenceScale` reads `cadenceMulBase / this`, which
    // is `1 / Πmul` — so the authored base cancels here by construction. See
    // `WeaponDef.cadenceMulBase`, and `ResolvedWeapon.cadence` for the two
    // consumers that do treat this as a rate and probably should not.
    cadence: def.cadenceMulBase * instance.rolled.cadenceMul,
    poise: def.poise,
    critChance: 0.05 + instance.rolled.critChanceAdd,
    critMul: 1.9 + instance.rolled.critMulAdd,
    primedDamageMul: 1,
    detonationMul: 1,
    lifestealFrac: 0,
    knockbackMul: 1,
    recoveryMul: 1,
    windupMul: 1,
    primes: def.primes.slice(),
    detonates: def.detonates.slice(),
    statuses: [],
    terrain: cloneTerrain(def.terrain),
    risks: [],
    riskMasks: [],
    triggers: [],
    triggerMask: 0,
    parryWindowMs: def.parry ? def.parry.windowMs : 0,
    projectile: {
      countAdd: 0, spreadMul: 1, speedMul: 1, pierceAdd: 0, ricochet: 0,
      homing: 0, gravityScaleMul: 1, lifetimeMul: 1, recoilMul: 1, splitOnImpact: 0,
    },
    melee: {
      arcMul: 1, trailMat: VOID, chargePerHit: 0, echoDelayMs: 0,
      extraChainHits: 0, parryWindowAdd: 0,
    },
    nodes: new Map(),
    entries: {
      light: def.combo[0], heavy: def.combo[0], dash: def.combo[0],
      air: def.combo[0], dair: def.combo[0],
    },
  };

  for (const n of def.combo) {
    r.nodes.set(n.id, n);
    if (n.entry === 'light' && r.entries.light === def.combo[0]) r.entries.light = n;
    else if (n.entry === 'heavy') r.entries.heavy = n;
    else if (n.entry === 'dash') r.entries.dash = n;
    else if (n.entry === 'air') {
      if (n.pogo) r.entries.dair = n;
      else r.entries.air = n;
    }
  }
  // combo[0] is the light entry by construction; the loop above only reassigns.
  r.entries.light = def.combo.find((n) => n.entry === 'light') ?? def.combo[0];

  for (const id of instance.modules) {
    const m = modules.get(id);
    if (!m) continue;
    if (m.fits !== 'both' && m.fits !== def.archetype) continue;
    const effect = def.archetype === 'melee' ? m.onMelee : m.onProjectile;
    applyEffect(r, effect, m);
    if (m.selfRisk) {
      r.risks.push(m.selfRisk);
      r.riskMasks.push(effect.trigger ? TRIGGER_FLAG_OF[effect.trigger] : 0);
    }
  }

  for (const t of r.triggers) r.triggerMask |= t.mask;
  if (def.parry) r.parryWindowMs = def.parry.windowMs + r.melee.parryWindowAdd;

  // The dig route is the weapon's damage type, stamped *after* the modules have
  // had their say — a pyre module retypes the blow, so it retypes the dig, and
  // that is the whole of "ammunition and weapon type mean the same thing to the
  // wall as they mean to the enemy" (#132). Stamped rather than authored so
  // there is exactly one place the two can disagree, and it is this line.
  r.terrain.damageType = r.damageType;

  return r;
}

function cloneTerrain(t: TerrainInteraction): TerrainInteraction {
  return {
    destroy: { ...t.destroy },
    damageType: t.damageType,
    emit: t.emit ? { ...t.emit } : undefined,
    heat: t.heat,
    charge: t.charge,
    corrode: t.corrode,
    displaces: t.displaces,
  };
}

function applyEffect(r: ResolvedWeapon, e: ModuleEffect, m: ModuleDef): void {
  // A conditional effect is registered, never folded.
  //
  // Folding it *and* registering it is what made `blood_trigger_sacrifice`
  // ("at low health every hit is a guaranteed critical") a permanent 100% crit
  // rate at full health, and `exotic_payload_martyr` a permanent ×3. docs/04 §9
  // forbids a module that is strictly better than another, and a condition that
  // costs nothing to satisfy is exactly that. Trigger payloads are read at hit
  // time by `collectTriggerMods` against the live event mask instead.
  if (e.trigger) {
    r.triggers.push({ event: e.trigger, mask: TRIGGER_FLAG_OF[e.trigger], effect: e, module: m });
    // Handling knobs a trigger declares still need somewhere to live; the parry
    // window is the one the state machine reads before any hit happens.
    if (e.melee?.parryWindowAdd) r.melee.parryWindowAdd += e.melee.parryWindowAdd;
    return;
  }

  if (e.damageMul !== undefined) r.damage *= e.damageMul;
  if (e.damageTypeOverride !== undefined) r.damageType = e.damageTypeOverride;
  if (e.cadenceMul !== undefined) r.cadence *= e.cadenceMul;
  if (e.recoveryMul !== undefined) r.recoveryMul *= e.recoveryMul;
  if (e.windupMul !== undefined) r.windupMul *= e.windupMul;
  if (e.knockbackMul !== undefined) r.knockbackMul *= e.knockbackMul;
  if (e.poiseMul !== undefined) r.poise *= e.poiseMul;
  if (e.reachMul !== undefined) r.reach *= e.reachMul;
  if (e.weightAdd !== undefined) r.weight = clamp(r.weight + e.weightAdd, 0.05, 1);
  if (e.critChanceAdd !== undefined) r.critChance += e.critChanceAdd;
  if (e.critMulAdd !== undefined) r.critMul += e.critMulAdd;
  if (e.primedDamageMul !== undefined) r.primedDamageMul *= e.primedDamageMul;
  if (e.detonationMul !== undefined) r.detonationMul *= e.detonationMul;
  if (e.lifestealFrac !== undefined) r.lifestealFrac += e.lifestealFrac;

  if (e.primes) for (const p of e.primes) if (!r.primes.includes(p)) r.primes.push(p);
  if (e.detonates) for (const p of e.detonates) if (!r.detonates.includes(p)) r.detonates.push(p);
  if (e.statusOnHit) r.statuses.push(e.statusOnHit);

  if (e.terrain) {
    const t = e.terrain;
    if (t.destroy) {
      r.terrain.destroy.maxHardness = Math.max(r.terrain.destroy.maxHardness, t.destroy.maxHardness);
      r.terrain.destroy.radius = Math.max(r.terrain.destroy.radius, t.destroy.radius);
      r.terrain.destroy.chance = Math.max(r.terrain.destroy.chance, t.destroy.chance);
    }
    if (t.heat !== undefined) r.terrain.heat = (r.terrain.heat ?? 0) + t.heat;
    if (t.charge !== undefined) r.terrain.charge = Math.max(r.terrain.charge ?? 0, t.charge);
    if (t.corrode !== undefined) r.terrain.corrode = Math.max(r.terrain.corrode ?? 0, t.corrode);
    if (t.displaces) r.terrain.displaces = true;
  }
  if (e.emit) r.terrain.emit = { ...e.emit };

  const p = e.projectile;
  if (p) {
    if (p.countAdd) r.projectile.countAdd += p.countAdd;
    if (p.spreadMul !== undefined) r.projectile.spreadMul *= p.spreadMul;
    if (p.speedMul !== undefined) r.projectile.speedMul *= p.speedMul;
    if (p.pierceAdd) r.projectile.pierceAdd += p.pierceAdd;
    if (p.ricochet) r.projectile.ricochet += p.ricochet;
    if (p.homing) r.projectile.homing = Math.max(r.projectile.homing, p.homing);
    if (p.gravityScaleMul !== undefined) r.projectile.gravityScaleMul *= p.gravityScaleMul;
    if (p.lifetimeMul !== undefined) r.projectile.lifetimeMul *= p.lifetimeMul;
    if (p.recoilMul !== undefined) r.projectile.recoilMul *= p.recoilMul;
    if (p.splitOnImpact) r.projectile.splitOnImpact += p.splitOnImpact;
  }

  const ml = e.melee;
  if (ml) {
    if (ml.arcMul !== undefined) r.melee.arcMul *= ml.arcMul;
    if (ml.trailMat !== undefined) r.melee.trailMat = ml.trailMat;
    if (ml.chargePerHit) r.melee.chargePerHit += ml.chargePerHit;
    if (ml.echoDelayMs) r.melee.echoDelayMs = ml.echoDelayMs;
    if (ml.extraChainHits) r.melee.extraChainHits += ml.extraChainHits;
    if (ml.parryWindowAdd) r.melee.parryWindowAdd += ml.parryWindowAdd;
  }
}

// ------------------------------------------------------------- trigger mods

/**
 * The scalar payload of whichever triggers are live for one hit. Module scope
 * and reused: `resolveHit` fills it twice per contact and must not allocate.
 */
export interface TriggerMods {
  damageMul: number;
  critChanceAdd: number;
  critMulAdd: number;
  detonationMul: number;
  primedDamageMul: number;
  lifestealFrac: number;
  poiseMul: number;
  knockbackMul: number;
}

const scratchMods: TriggerMods = {
  damageMul: 1, critChanceAdd: 0, critMulAdd: 0, detonationMul: 1,
  primedDamageMul: 1, lifestealFrac: 0, poiseMul: 1, knockbackMul: 1,
};

/** Second buffer: the crit and detonate passes run while `scratchMods` is live. */
const scratchCrit: TriggerMods = {
  damageMul: 1, critChanceAdd: 0, critMulAdd: 0, detonationMul: 1,
  primedDamageMul: 1, lifestealFrac: 0, poiseMul: 1, knockbackMul: 1,
};

function resetMods(o: TriggerMods): TriggerMods {
  o.damageMul = 1; o.critChanceAdd = 0; o.critMulAdd = 0; o.detonationMul = 1;
  o.primedDamageMul = 1; o.lifestealFrac = 0; o.poiseMul = 1; o.knockbackMul = 1;
  return o;
}

/** Accumulate every trigger of `wp` whose event is set in `active`. */
export function collectTriggerMods(
  wp: ResolvedWeapon,
  active: TriggerMask,
  out: TriggerMods,
): TriggerMods {
  resetMods(out);
  if ((wp.triggerMask & active) === 0) return out;
  for (const t of wp.triggers) {
    if ((t.mask & active) === 0) continue;
    const e = t.effect;
    if (e.damageMul !== undefined) out.damageMul *= e.damageMul;
    if (e.critChanceAdd !== undefined) out.critChanceAdd += e.critChanceAdd;
    if (e.critMulAdd !== undefined) out.critMulAdd += e.critMulAdd;
    if (e.detonationMul !== undefined) out.detonationMul *= e.detonationMul;
    if (e.primedDamageMul !== undefined) out.primedDamageMul *= e.primedDamageMul;
    if (e.lifestealFrac !== undefined) out.lifestealFrac += e.lifestealFrac;
    if (e.poiseMul !== undefined) out.poiseMul *= e.poiseMul;
    if (e.knockbackMul !== undefined) out.knockbackMul *= e.knockbackMul;
  }
  return out;
}

/**
 * Which conditional events are true for this contact, before the crit roll and
 * before detonation — those two are decided mid-resolution and OR-ed in later.
 */
function baseTriggerMask(w: World, hit: HitInput): TriggerMask {
  let m = TriggerFlag.HIT | hit.windows;
  const a = hit.attacker;
  if (w.maxHp[a] > 0 && w.hp[a] / w.maxHp[a] <= LOW_HEALTH_FRAC) m |= TriggerFlag.LOW_HEALTH;
  const entry = hit.node?.entry;
  if (entry === 'heavy') m |= TriggerFlag.FINISHER;
  if (entry === 'dash') m |= TriggerFlag.DASH;
  if (anyPrimer(w, hit.target)) m |= TriggerFlag.PRIMER;
  return m;
}

// ============================================ material-derived effectiveness

/**
 * How well `type` works against a single material, read entirely off the
 * material's own physical properties. Nothing here names an enemy.
 */
export function typeEffectivenessVs(mat: MatId, type: DamageTypeId): number {
  const tg = M.tags[mat];
  let e = 1;

  switch (type) {
    case DamageType.FIRE:
      if (tg & Tag.FLAMMABLE) e += 0.9;
      if (tg & Tag.ACCELERANT) e += 0.7;
      if (tg & Tag.CRYO) e += 0.8;
      if (tg & Tag.PYRO) e -= 0.7;
      if (tg & Tag.SEALED) e -= 0.2;
      if (tg & Tag.VOID) e -= 0.5;
      // A high heat capacity is literally "takes a lot of energy to hurt".
      e *= 1.1 / (0.5 + M.heatCapacity[mat] * 0.6);
      if (M.ignitionTemp[mat] < 400) e += 0.4;
      break;

    case DamageType.FROST:
      if (tg & (Tag.ORGANIC | Tag.BIOMASS | Tag.BLOOD | Tag.LIVING)) e += 0.6;
      if (tg & Tag.PYRO) e += 0.9;
      if (tg & Tag.CRYO) e -= 0.75;
      if (tg & Tag.METAL) e += 0.25;
      e += M.conductivity[mat] * 0.4;
      // Freezing works by pulling heat out; low capacity gives way faster.
      e *= 1.05 / (0.55 + M.heatCapacity[mat] * 0.5);
      break;

    case DamageType.SHOCK:
      e = 0.7 + M.electricConductivity[mat] * 1.8;
      if (tg & Tag.INSULATOR) e *= 0.3;
      if (tg & Tag.MACHINE) e += 0.6;
      if (M.shortCircuits[mat] === 1) e += 0.5;
      e *= 1 - M.chargeResistance[mat] * 0.5;
      break;

    case DamageType.ACID:
      e = 0.6 + (1 - M.corrosionResist[mat]) * 1.4;
      if (tg & Tag.METAL) e += 0.35;
      if (tg & (Tag.CRYSTAL | Tag.CERAMIC)) e -= 0.45;
      // Already corrosive things do not mind more acid.
      e *= 1 - M.corrosiveness[mat] * 0.6;
      break;

    case DamageType.TOXIC:
      e = 0.35 + M.bioActivity[mat] * 1.6;
      if (tg & (Tag.ORGANIC | Tag.LIVING | Tag.BIOMASS)) e += 0.6;
      if (tg & (Tag.MACHINE | Tag.METAL | Tag.STONE)) e -= 0.55;
      if (tg & Tag.UNDEAD) e -= 0.5;
      e *= 1 - M.toxicity[mat] * 0.5;
      break;

    case DamageType.VOID:
      // Void does not care how hard you are; it cares whether you are anchored.
      e = 1.0 + (1 - M.temporalStability[mat]) * 0.5;
      if (tg & Tag.SACRED) e -= 0.55;
      if (tg & Tag.VOID) e -= 0.7;
      if (tg & (Tag.STONE | Tag.METAL)) e += 0.25;
      e *= 1 - M.sanctity[mat] * 0.5;
      break;

    case DamageType.RADIANT:
      if (tg & Tag.UNDEAD) e += 1.3;
      if (tg & Tag.CURSED) e += 1.1;
      if (tg & Tag.VOID) e += 1.4;
      if (tg & Tag.SACRED) e -= 0.7;
      e *= 1 - M.sanctity[mat] * 0.7;
      break;

    case DamageType.BLOOD:
      e = 0.3;
      if (tg & (Tag.ORGANIC | Tag.BLOOD | Tag.LIVING | Tag.BIOMASS)) e += 1.1;
      if (tg & (Tag.MACHINE | Tag.METAL)) e -= 0.15;
      if (tg & Tag.UNDEAD) e -= 0.1;
      break;

    case DamageType.TEMPORAL:
      e = 0.6 + (1 - M.temporalStability[mat]) * 1.6;
      e += M.resonance[mat] * 0.3;
      break;

    case DamageType.FUNGAL:
      e = 0.4 + M.bioActivity[mat] * 1.3;
      if (tg & (Tag.ORGANIC | Tag.BIOMASS)) e += 0.6;
      if (tg & Tag.POROUS) e += 0.45;
      if (tg & Tag.SEALED) e -= 0.4;
      if (tg & (Tag.MACHINE | Tag.METAL)) e -= 0.5;
      if (tg & Tag.FUNGAL) e -= 0.5;
      break;

    case DamageType.NANITE:
      e = 0.5;
      if (tg & Tag.MACHINE) e += 1.4;
      if (tg & Tag.METAL) e += 0.7;
      if (tg & Tag.CONDUCTIVE) e += 0.3;
      if (tg & Tag.NANITE) e -= 0.35;
      if (tg & Tag.VOID) e -= 0.3;
      break;

    case DamageType.PHYSICAL:
    default:
      // Hardness resists, brittleness invites.
      e = 1.3 / (0.55 + M.hardness[mat] * 0.2);
      e += M.brittleness[mat] * 0.6;
      if (tg & Tag.VOID) e -= 0.6;
      break;
  }

  return clamp(e, 0.05, 3);
}

// ---------------------------------------------------- dig routes (#132, §3.2)

/**
 * A damage type is a **route** into a material when it scores at least this
 * much against it. Neutral is 1.0, so 1.25 reads as "a quarter better than
 * nothing in particular".
 *
 * docs/15 §3.2 argues the number. It is not a tuning dial: moving it changes
 * which walls name which keys, which is a design statement, and
 * `tests/balance.test.ts` reddens if it stops leaving every material at least
 * `DIG_MIN_ROUTES` ways in.
 */
export const DIG_ROUTE_EFF = 1.25;

/** How much the right damage type is worth against a wall. */
export const DIG_TYPE_ROUTE = 2;

/** How much an already-weakened cell is worth — frozen, burning, corroding. */
export const DIG_STATE_ROUTE = 1.5;

/**
 * The most keys one wall may name. #114's finding: the failure mode is too many
 * distinctions at once. Four is what fits on a codex line and in a player's
 * head; the types past it are the ones they would never have reached for.
 */
export const DIG_ROUTE_MAX_TYPES = 4;

/** Cell states that count as "already weakened" — the third route. */
export const DIG_WEAKENED_FLAGS = Flag.FROZEN | Flag.BURNING | Flag.CORRODING;

const DAMAGE_TYPE_COUNT = DAMAGE_TYPE_NAMES.length;

/**
 * `routeMask[mat]` — bit `t` set when damage type `t` is a route into `mat`.
 *
 * Rebuilt when the registry's size changes, which is the only way the answer
 * can move inside one process. Memoised because `applyTerrain` asks per cell
 * per active frame and `typeEffectivenessVs` is a twelve-way switch over eight
 * `M.*` reads.
 */
let routeMask: Int32Array | null = null;
let routeMaskFor = -1;

function buildRouteMask(): Int32Array {
  const n = M.hardness.length;
  const out = new Int32Array(n);
  for (let mat = 0; mat < n; mat++) {
    // Score every type, keep the ones above the bar, and take the best
    // `DIG_ROUTE_MAX_TYPES` of those. Sorted by score so the cap drops the
    // marginal keys rather than the low-numbered ones.
    const scored: { t: number; e: number }[] = [];
    for (let t = 0; t < DAMAGE_TYPE_COUNT; t++) {
      const e = typeEffectivenessVs(mat, t as DamageTypeId);
      if (e >= DIG_ROUTE_EFF) scored.push({ t, e });
    }
    scored.sort((a, b) => b.e - a.e || a.t - b.t);
    let m = 0;
    for (const s of scored.slice(0, DIG_ROUTE_MAX_TYPES)) m |= 1 << s.t;
    out[mat] = m;
  }
  return out;
}

function routeMaskOf(mat: MatId): number {
  if (routeMask === null || routeMaskFor !== M.hardness.length) {
    routeMask = buildRouteMask();
    routeMaskFor = M.hardness.length;
  }
  return routeMask[mat] ?? 0;
}

/**
 * The damage types that open a material, best first — what a wall's codex entry
 * lists and what `digRouteMul` reads.
 *
 * **This is not a second effectiveness table.** It is a threshold over
 * `typeEffectivenessVs`, the same function `compositionEffectiveness` averages
 * over an enemy's four materials. docs/15 §3.2 states why terrain reads it
 * directly rather than through a parallel table: two tables for one concept is
 * the id-drift failure in numeric form, and this project has paid for it twice.
 */
export function digRoutesOf(mat: MatId): DamageTypeId[] {
  const mask = routeMaskOf(mat);
  const out: DamageTypeId[] = [];
  for (let t = 0; t < DAMAGE_TYPE_COUNT; t++) {
    if (mask & (1 << t)) out.push(t as DamageTypeId);
  }
  out.sort((a, b) => typeEffectivenessVs(mat, b) - typeEffectivenessVs(mat, a) || a - b);
  return out;
}

/** True when `type` is one of `mat`'s keys. */
export function isDigRoute(mat: MatId, type: DamageTypeId): boolean {
  return (routeMaskOf(mat) & (1 << type)) !== 0;
}

// ------------------------------------------------- cue families (#132, #137)

/**
 * One of the families a player is asked to hold in their head — docs/15 §3.2.
 *
 * A family is a *look* (`tell`) plus the damage types that usually open the
 * things that look like that (`keys`). It is a **description of
 * `typeEffectivenessVs`, not a second table**: nothing here is consulted by
 * `digRouteMul`, `resolveHit` or anything else on the damage path, and
 * `tests/balance.test.ts` recomputes the coverage claim off the shared function
 * rather than trusting the lists below.
 */
export interface CueFamily {
  readonly name: string;
  /** The look. What a player is supposed to notice before swinging. */
  readonly tell: string;
  /** Material tags this family claims. `0` is the remainder and matches all. */
  readonly mask: number;
  /** The types that usually work, best first. */
  readonly keys: readonly DamageTypeId[];
}

/**
 * The cue families, **in match order** — first mask hit wins, and the last
 * entry's mask is `0` because `stone` is the remainder.
 *
 * Order is load-bearing and is the only thing distinguishing four families from
 * five: `grave` sits above `glass` and `quick` so that voidglass reads as a
 * grave thing rather than as a window, and bone as a grave thing rather than as
 * meat. Moving it below either one silently returns the four-family answer.
 *
 * **Why the fifth family exists**, against #114's "too many distinctions at
 * once" and §3.2's own *four cues, because nine would be none*: the four
 * families were derived from terrain, terrain has no undead, and `radiant`
 * therefore appeared in exactly one key list. docs/15 §2.1.2 has the
 * measurement that bought it — including what it costs.
 */
export const CUE_FAMILIES: readonly CueFamily[] = [
  {
    name: 'metal',
    tell: 'sheen, conducts',
    mask: Tag.METAL,
    keys: [DamageType.SHOCK, DamageType.ACID, DamageType.FROST, DamageType.NANITE],
  },
  {
    name: 'grave',
    tell: 'grey-white, cold-lit, never warm',
    mask: Tag.UNDEAD | Tag.CURSED | Tag.VOID,
    keys: [DamageType.RADIANT, DamageType.PHYSICAL, DamageType.ACID],
  },
  {
    // `radiant` was in this list until #137 and is not any more. It was here for
    // VOIDGLASS and CHOIRLIGHT, both of which `grave` now claims, and after that
    // move no ceramic or crystal in the registry has radiant as a route at all —
    // so leaving it would be the first authored (rather than described) entry in
    // the cue table. `tests/balance.test.ts` is what noticed.
    name: 'glass',
    tell: 'translucent, emissive, cracks',
    mask: Tag.CERAMIC | Tag.CRYSTAL,
    keys: [DamageType.PHYSICAL, DamageType.SHOCK],
  },
  {
    name: 'quick',
    tell: 'fibrous, warm, wet',
    mask: Tag.ORGANIC | Tag.BIOMASS | Tag.LIVING | Tag.FUNGAL | Tag.BLOOD,
    keys: [
      DamageType.FIRE, DamageType.ACID, DamageType.TOXIC,
      DamageType.FUNGAL, DamageType.BLOOD, DamageType.PHYSICAL,
    ],
  },
  { name: 'stone', tell: 'grey, opaque, matte', mask: 0, keys: [DamageType.PHYSICAL, DamageType.ACID, DamageType.VOID] },
];

/**
 * The family one material reads as — what a **wall** is cued by.
 *
 * There is one of these functions and both sides call it. #136 was two tables
 * for one concept; a second `switch` over tags "so the codex can group enemies
 * its own way" would be the same failure in the same place, one layer up.
 */
export function cueFamilyOf(mat: MatId): CueFamily {
  const tg = M.tags[mat];
  for (const f of CUE_FAMILIES) {
    if (f.mask === 0 || (tg & f.mask) !== 0) return f;
  }
  return CUE_FAMILIES[CUE_FAMILIES.length - 1];
}

/**
 * The family a **body** reads as — docs/15 §2.1: *a body's cue is #132's family
 * of its `coreMat`*.
 *
 * The core and not the whole composition, because a cue is a thing you see
 * before the fight and the core is what the body is *made of* in the sense a
 * silhouette can carry. It is deliberately the same function terrain calls, on
 * one material id, so the enemy cue cannot drift from the wall cue.
 */
export function bodyCueFamilyOf(coreMat: MatId): CueFamily {
  return cueFamilyOf(coreMat);
}

/**
 * What the blow's routes multiply the digger's edge by — docs/15 §3.2.
 *
 * `undefined` means the caller has no damage type to offer (a burrower, an
 * ability, the harvest sweep before a weapon is resolved) and gets force alone,
 * which is exactly the pre-#132 behaviour.
 *
 * The two route kinds multiply because they are independent claims about the
 * same wall: the right key is worth the same again whether or not somebody
 * already froze it.
 */
export function digRouteMul(
  mat: MatId,
  flags: number,
  type: DamageTypeId | undefined,
): number {
  let mul = 1;
  if (type !== undefined && isDigRoute(mat, type)) mul *= DIG_TYPE_ROUTE;
  // One multiplier however many of the three states are set: "weakened" is one
  // claim, not three, and billing it per flag would make a burning corroding
  // cell 2.25× for no stated reason.
  if ((flags & DIG_WEAKENED_FLAGS) !== 0) mul *= DIG_STATE_ROUTE;
  return mul;
}

/**
 * Destroy one cell at the edge the blow's routes buy it.
 *
 * The gate itself is still `CellWorld.damageCell`'s and still reads
 * `M.hardness[m] > power` — #130's finding is intact, digging is a threshold
 * and not a rate. What #132 changed is what supplies `power`.
 */
export function digCell(
  cells: CellWorld,
  x: number,
  y: number,
  power: number,
  type: DamageTypeId | undefined,
): boolean {
  const m = cells.peek(x, y);
  if (m === VOID) return false;
  return cells.damageCell(x, y, power * digRouteMul(m, cells.getFlags(x, y), type));
}

/**
 * What a body's whole matchup is worth, either way — docs/15 §2.1, #135.
 *
 * A body may be resistant. It may not be immune, and it may not be a deletion.
 * The right damage type is worth `DIG_TYPE_ROUTE` and the wrong one costs the
 * same again, so a matchup spans ×4 end to end and no more.
 *
 * **It is deliberately the same number §3.2 prices a terrain route at.**
 * `typeEffectivenessVs` is one table with one opinion, and #132 already decided
 * what a route into it is worth: ×2. A wall and a body are the same question
 * asked of one material and of four, so they are priced the same. The
 * alternative was two prices for one claim, which is the id-drift failure in
 * numeric form and the exact thing §3.2 refused to author.
 *
 * **What this replaces was never chosen.** Before #135 the enemy side inherited
 * whatever `clamp(e, 0.05, 3)` at the bottom of `typeEffectivenessVs` happened
 * to allow — a ×60 window whose only stated job is keeping the arithmetic
 * finite. A numeric guard is not a design statement, and this one had quietly
 * become the widest balance term in the game: per-boss TTK swung ×4.4 to ×43.1
 * on it, `the_stillcore` taking 3.8 s from `cleaver_axe` and 163.5 s from
 * `hunger_symbiote`.
 *
 * **The clamp is here and not in `typeEffectivenessVs`, and that is the whole
 * of the coupling care.** The claim being priced is about a *body*; a single
 * material's score is terrain's question, #132 answers it with a threshold at
 * `DIG_ROUTE_EFF` and a flat `DIG_TYPE_ROUTE`, and it needs the full dynamic
 * range to sort keys best-first. So `typeEffectivenessVs`, `digRoutesOf`,
 * `digRouteMul` and every figure in §3.2 are untouched to the last bit, which
 * `tests/balance.test.ts` asserts rather than assumes.
 */
export const MATCHUP_CEILING = DIG_TYPE_ROUTE;

/** The other side of the same claim: the wrong key costs what the right one buys. */
export const MATCHUP_FLOOR = 1 / MATCHUP_CEILING;

/** The extra share the core material carries over the rest of the body. */
export const CORE_SHARE = 0.35;

/**
 * The one body-matchup reading — docs/15 §2.1, #136.
 *
 * Weighted effectiveness against a whole composition: `typeEffectivenessVs`
 * averaged over up to four materials, with the core counting for an extra
 * `CORE_SHARE`, which is what makes "it has a steel core under the flesh" mean
 * something. Clamped to `[MATCHUP_FLOOR, MATCHUP_CEILING]` — see above.
 *
 * **Why this takes loose arrays rather than a `World`.** There are two callers
 * with two shapes of the same body: combat holds it in the packed ECS arrays,
 * and the codex holds it as an `EnemyDef` before anything is spawned. Before
 * #136 that difference was answered by writing the average out twice, and the
 * second copy drifted into a second *table* — `enemyData.materialFactor`, which
 * nothing on the damage path ever called and which the codex published. It
 * disagreed with this function by more than ×2 on 134 of 648 body × type pairs
 * and named a different top weakness for 37 of the 54 bodies.
 *
 * So the merge is at the level of the whole reading and not just the table.
 * Sharing `typeEffectivenessVs` alone would have left the two weightings
 * forked — this one gives the core `frac + CORE_SHARE`, the deleted one gave it
 * `frac × 1.35` — which is the same failure one decimal quieter. There is one
 * body answer and both callers are thin adapters onto it.
 *
 * @param mats  four material ids at `base .. base+3`; `VOID` for an empty slot.
 * @param fracs four fractions at the same offsets; `<= 0` for an empty slot.
 * @param core  the core material, or `VOID` for a body that names none.
 */
export function compositionEffectivenessOf(
  mats: ArrayLike<number>,
  fracs: ArrayLike<number>,
  base: number,
  core: MatId,
  type: DamageTypeId,
): number {
  let sum = 0;
  let total = 0;
  for (let k = 0; k < 4; k++) {
    const frac = fracs[base + k];
    if (frac <= 0) continue;
    sum += typeEffectivenessVs(mats[base + k], type) * frac;
    total += frac;
  }
  if (core !== VOID) {
    sum += typeEffectivenessVs(core, type) * CORE_SHARE;
    total += CORE_SHARE;
  }
  if (total <= 0) return 1;
  return clamp(sum / total, MATCHUP_FLOOR, MATCHUP_CEILING);
}

/**
 * The ECS adapter onto `compositionEffectivenessOf` — what `resolveHit`,
 * `enemyAttacks` and `profileOf` read.
 */
export function compositionEffectiveness(w: World, i: number, type: DamageTypeId): number {
  return compositionEffectivenessOf(w.compMat, w.compFrac, i * 4, w.coreMat[i], type);
}

/**
 * What the cell the target is standing in does to the blow.
 *
 * docs/03 §6 says effectiveness comes from what a thing is made of; docs/04 §1
 * says priming lives in the world and not only on bodies. Together those mean
 * the *floor* is part of the defender. Hitting a wet enemy with lightning was
 * numerically identical to hitting a dry one, which quietly deleted the single
 * most legible interaction in the game.
 *
 * Read once, from the footprint, so the answer costs four cell reads rather
 * than a per-frame scan.
 */
export function environmentEffectiveness(
  w: World,
  cells: CellWorld,
  i: number,
  type: DamageTypeId,
): number {
  const x = Math.floor(w.x[i]);
  const feet = Math.floor(w.y[i] + w.halfH[i] - 1);
  const bodyFlags = cells.getFlags(x, Math.floor(w.y[i]));
  const feetFlags = cells.getFlags(x, feet);
  const flags = bodyFlags | feetFlags;
  const standing = cells.peek(x, feet);
  const wet = (flags & Flag.WET) !== 0 ||
    (standing !== VOID && M.electricConductivity[standing] > 0.4 &&
      M.category[standing] === Category.LIQUID);

  let e = 1;
  switch (type) {
    case DamageType.SHOCK:
      // Standing in the puddle is the whole point of the puddle.
      if (wet) e *= 1.75;
      if (flags & Flag.CHARGED) e *= 1.4;
      break;
    case DamageType.FIRE:
      if (wet) e *= 0.55;
      if (flags & Flag.BURNING) e *= 1.35;
      if (flags & Flag.FROZEN) e *= 0.8;
      break;
    case DamageType.FROST:
      // Water conducts heat out of a body far faster than air does.
      if (wet) e *= 1.5;
      if (flags & Flag.BURNING) e *= 0.6;
      break;
    case DamageType.PHYSICAL:
      // A body frozen to the floor has nowhere to give.
      if (flags & Flag.FROZEN) e *= 1.25;
      break;
    case DamageType.ACID:
      if (flags & Flag.CORRODING) e *= 1.3;
      if (wet) e *= 0.85; // diluted
      break;
    default:
      break;
  }
  return clamp(e, 0.25, 3);
}

// =================================================== terrain primer reading

/**
 * A primer sitting in the world rather than on a body. This is the bridge
 * between buildcraft and the material sim: the oil on the floor is as valid a
 * detonation target as the oil on the enemy.
 */
export function terrainPrimerAt(cells: CellWorld, x: number, y: number): PrimerKindId | 0 {
  const flags = cells.getFlags(x, y);
  if (flags & Flag.BURNING) return PrimerKind.BURNING;
  if (flags & Flag.FROZEN) return PrimerKind.FROZEN;
  if (flags & Flag.CHARGED) return PrimerKind.SHOCKED;
  if (flags & Flag.CURSED) return PrimerKind.CURSED;
  if (flags & Flag.INFECTED) return PrimerKind.INFECTED;
  if (flags & Flag.PHASED) return PrimerKind.PHASED;

  const m = cells.peek(x, y);
  if (m === VOID) return 0;
  const tg = M.tags[m];
  // Slick and flammable together is oil, tar, or anything that behaves like it.
  if ((tg & Tag.SLICK) && (tg & Tag.FLAMMABLE)) return PrimerKind.OILED;
  if (tg & Tag.PYRO) return PrimerKind.BURNING;
  if (tg & Tag.CRYO) return PrimerKind.FROZEN;
  if (M.corrosiveness[m] > 0.3) return PrimerKind.CORRODED;
  if (tg & Tag.BLOOD) return PrimerKind.BLEEDING;
  if (tg & Tag.FUNGAL) return PrimerKind.INFECTED;
  if (tg & Tag.CURSED) return PrimerKind.CURSED;
  if (tg & Tag.TEMPORAL) return PrimerKind.PHASED;
  if (M.electricConductivity[m] > 0.5) return PrimerKind.SHOCKED;
  return 0;
}

/** Scan a small neighbourhood for a primer this weapon knows how to detonate. */
function findTerrainPrimer(
  cells: CellWorld,
  x: number,
  y: number,
  radius: number,
  detonates: readonly PrimerKindId[],
): PrimerKindId | 0 {
  if (detonates.length === 0) return 0;
  const r = Math.max(1, Math.min(6, Math.round(radius)));
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      const p = terrainPrimerAt(cells, x + dx, y + dy);
      if (p !== 0 && detonates.includes(p)) return p;
    }
  }
  return 0;
}

// =========================================================== hit resolution

export interface HitInput {
  attacker: number;
  target: number;
  weapon: ResolvedWeapon;
  node: ComboNode | null;
  hand: Hand;
  /** Impact point in world units. */
  hx: number;
  hy: number;
  /** Unit direction of the blow. */
  dirX: number;
  dirY: number;
  stats: CombatStats;
  /** Rhythm meter, 0..1 (docs/04 §8). */
  rhythm: number;
  /** Charge tier scalar, 1 when uncharged. */
  charge: number;
  backstab: boolean;
  /** Suppresses iframe granting — for DoT-like repeated contact (beams, drills). */
  continuous: boolean;
  /**
   * Timed trigger events the wielder has open right now: `onParry`, `onSwap`
   * and `onKill` are windows rather than properties of the contact, so they are
   * latched by the state machine and read here.
   */
  windows: TriggerMask;
  /** Primer from the charge tier that produced this swing; 0 when uncharged. */
  chargePrimer: PrimerKindId | 0;
  /**
   * This contact is a delayed duplicate of an earlier one (`melee.echoDelayMs`)
   * rather than a fresh blow.
   *
   * It suppresses detonation, and that is the whole reason the flag exists. The
   * original swing applies `ComboNode.primerApplied`; an echo that resolved as a
   * fresh blow would then detonate the primer its own strike had just laid, on
   * every swing, solo and for free. That is precisely the §10 anti-pattern the
   * `primes`-is-a-declaration rule was written for, moved 400 ms downfield.
   */
  echo: boolean;
}

export interface HitResult {
  hit: boolean;
  damage: number;
  /**
   * What this contact is *worth*: `damage` with the crit coin un-flipped.
   *
   * `damage` is one sample of a random variable — this contact either crit or
   * it did not — and a balance statement is about the mean of that variable,
   * not about one draw. Nothing outside a fixture should read this; the game
   * subtracts `damage`.
   *
   * It exists because the alternative is a *second* model. `docs/15` §3 asserts
   * the melee spread analytically across 17 classes, and the analytic form used
   * to recompute damage as `wp.damage × node.damageMul` — a transcription of
   * the first eight lines of this function that silently omitted crits, the
   * primed bonus, self-detonation, brittleness and the backstab, and was 59%
   * wrong about `flense_dagger` (#50). A model that *reads* this number instead
   * cannot omit a term, because it is not the one computing them. It is the
   * same move as `nodeTiming`, `hitRateHz` and `bodyQuad`: if a test has to
   * know a number, the runtime exports it rather than the test re-deriving it.
   *
   * Exact rather than sampled: every term downstream of the crit multiply —
   * detonation, the frozen-shatter bonus — is linear in `dmg`, so dividing out
   * the factor the roll applied and multiplying in its expectation is the
   * whole calculation, and it needs no repetitions to converge. The one
   * non-linearity is `shatterFrozen`'s threshold on `dmg`, which only a FROZEN
   * target can reach; against one, read this as an approximation.
   */
  expectedDamage: number;
  damageType: DamageTypeId;
  effectiveness: number;
  crit: boolean;
  detonated: PrimerKindId | 0;
  detonationDamage: number;
  shattered: boolean;
  killed: boolean;
  staggered: boolean;
  hitstop: number;
  knockX: number;
  knockY: number;
  lifesteal: number;
  selfDamage: number;
}

/** Reusable result. Callers that need to keep one must copy it. */
export function newHitResult(): HitResult {
  return {
    hit: false, damage: 0, expectedDamage: 0,
    damageType: DamageType.PHYSICAL, effectiveness: 1,
    crit: false, detonated: 0, detonationDamage: 0, shattered: false,
    killed: false, staggered: false, hitstop: 0, knockX: 0, knockY: 0,
    lifesteal: 0, selfDamage: 0,
  };
}

function reset(o: HitResult): HitResult {
  o.hit = false; o.damage = 0; o.expectedDamage = 0;
  o.effectiveness = 1; o.crit = false;
  o.detonated = 0; o.detonationDamage = 0; o.shattered = false;
  o.killed = false; o.staggered = false; o.hitstop = 0;
  o.knockX = 0; o.knockY = 0; o.lifesteal = 0; o.selfDamage = 0;
  return o;
}

/**
 * Iframe duration granted by a hit, in seconds. Light hits grant less.
 *
 * Exported because it is not only an anti-double-hit detail: it is the
 * **ceiling on single-target hit rate**. `resolveHit` returns early while
 * `w.iframes[t]` runs, so no discrete weapon can land on one body faster than
 * `1 / iframeFor(weight)` however short its nodes are — and the two fastest
 * classes are authored well inside that ceiling. `flense_dagger` runs a
 * 58/56/73 ms chain against a 139 ms window and therefore lands **two nodes in
 * three**, unconditionally, against every body in the game.
 *
 * The analytic DPS model in `tests/balance.test.ts` was multiplying all three,
 * which is the second half of #50: the dagger's model error was a +2.5×
 * backstab and a −1/3 hit rate partly cancelling. A model that reads this
 * function cannot make that mistake again in one direction without the other.
 */
export function iframeFor(weight: number): number {
  return 0.12 + weight * 0.16;
}

/**
 * Resolve one contact. Writes into `out` and returns it — the hot path calls
 * this many times per frame and must not allocate.
 */
export function resolveHit(
  w: World,
  cells: CellWorld,
  hit: HitInput,
  out: HitResult,
  rng: Rng,
): HitResult {
  reset(out);
  const { attacker: a, target: t, weapon: wp } = hit;

  if (w.iframes[t] > 0 && !hit.continuous) return out;
  if (w.hp[t] <= 0) return out;

  const node = hit.node;
  const nodeDmg = node ? node.damageMul : 1;
  const nodePoise = node ? node.poiseMul : 1;

  // Conditional module payloads, evaluated against what is true right now.
  const active = baseTriggerMask(w, hit);
  const mods = collectTriggerMods(wp, active, scratchMods);

  // --- base
  let dmg = wp.damage * nodeDmg * hit.charge * hit.stats.power;
  dmg *= 1 + statScaling(wp.def, hit.stats);
  // Alternating hands is the mechanic that makes this a dual-wield game, so
  // the rhythm bonus has to be worth actually playing around.
  dmg *= 1 + hit.rhythm * 0.35;
  dmg *= mods.damageMul;

  // --- the weapon's own resource (docs/04 §11)
  dmg *= chargePayout(wp, node);

  // --- composition, then the cell the composition is standing in
  out.damageType = wp.damageType;
  const eff =
    compositionEffectiveness(w, t, wp.damageType) *
    environmentEffectiveness(w, cells, t, wp.damageType);
  out.effectiveness = eff;
  dmg *= eff;

  // --- defender statuses
  dmg *= damageTakenMul(w, t);
  if (wp.damageType === DamageType.PHYSICAL) dmg *= brittleMul(w, t);

  // --- primed bonus (any primer at all, before detonation)
  if ((active & TriggerFlag.PRIMER) !== 0) dmg *= wp.primedDamageMul * mods.primedDamageMul;

  // --- crit
  //
  // The multiplier is computed whether or not the coin comes up, because two
  // readers need it: this contact, which either gets it or does not, and
  // `out.expectedDamage`, which is what the contact was worth before the coin
  // was flipped. Computing it once is what keeps those two from drifting —
  // the analytic balance model reads the second, and the version of it that
  // re-derived damage from `wp.damage × node.damageMul` had no crit term at
  // all and was 59% wrong about the dagger (#50).
  let critChance = wp.critChance + hit.stats.critChance + mods.critChanceAdd;
  if (hasStatus(w, t, StatusKind.MARKED)) critChance += 0.2;
  // A backstab is the dagger's stated identity ("Backstabs for ×2.5") and it
  // moves *two* terms, which is easy to miss: the chance as well as the
  // damage.
  if (hit.backstab) critChance += 0.5;
  const critP = clamp(critChance, 0, 1);
  let critMul = wp.critMul * hit.stats.critMul + mods.critMulAdd;
  // onCrit triggers are a payload of the crit, so they multiply the crit
  // branch rather than the base — folding them into `critMul` keeps that true
  // and keeps the expectation below exact.
  if ((wp.triggerMask & TriggerFlag.CRIT) !== 0) {
    critMul *= collectTriggerMods(wp, TriggerFlag.CRIT, scratchCrit).damageMul;
  }
  const rolledCrit = rng.chance(critP);
  if (rolledCrit) {
    out.crit = true;
    dmg *= critMul;
  }
  /** `damage / critApplied` recovers the pre-roll figure; this is its mean. */
  const critExpectation = (1 - critP + critP * critMul) / (rolledCrit ? critMul : 1);
  if (hit.backstab) dmg *= 2.5;

  // --- detonation (§1): the reason to hold two different weapons
  const primer = hit.echo ? 0 : findPrimer(w, cells, hit);
  if (primer !== 0) {
    out.detonated = primer;
    let detMul = wp.detonationMul * mods.detonationMul;
    if ((wp.triggerMask & TriggerFlag.DETONATE) !== 0) {
      detMul *= collectTriggerMods(wp, TriggerFlag.DETONATE, scratchCrit).detonationMul;
    }
    out.detonationDamage = detonate(w, cells, hit, primer, dmg, detMul, rng);
    dmg += out.detonationDamage;
  }

  // --- FROZEN bodies come apart instead of taking the hit
  if (shatterFrozen(w, cells, t, dmg, rng)) {
    out.shattered = true;
    dmg *= 1.8;
  }

  dmg = Math.max(0, dmg);
  out.hit = true;
  out.damage = dmg;
  // Everything between the crit multiply and here — detonation, the shatter
  // bonus — is linear in `dmg`, so one scalar carries the whole expectation.
  out.expectedDamage = dmg * critExpectation;
  w.hp[t] -= dmg;
  out.killed = w.hp[t] <= 0;

  // --- poise & stagger
  const poise = wp.poise * nodePoise * mods.poiseMul * (1 + hit.rhythm * 0.2);
  w.poise[t] -= poise;
  if (w.poise[t] <= 0) {
    w.poise[t] = w.maxPoise[t];
    const dur = 0.25 + wp.weight * 0.55;
    w.stagger[t] = Math.max(w.stagger[t], dur);
    applyStatus(w, t, StatusKind.STAGGERED, dur, 1);
    out.staggered = true;
  }

  // --- hitstop: no longer symmetric, because the two sides are not the same
  //     kind of thing.
  //
  //     The target's freeze is the impact read docs/02 §4 argues for — a heavy
  //     weapon should visibly stop what it hit for longer — so it stays scaled
  //     by weight, 40–90 ms, unchanged.
  //
  //     The attacker's freeze is not a feel knob at all: `updateWielder` returns
  //     early while it runs, so it is time the hand cannot swing in, i.e. a
  //     straight multiplier on the weapon's DPS. Charging it at the target's
  //     weight-scaled rate cost the 16 discrete melee classes between 23.0% and
  //     42.5% of their throughput — a 19.5 pp spread in effective damage that no
  //     band in any doc mentions, and one that runs *against* weight, because
  //     the spread comes from the cycle length varying 4.6× while the freeze
  //     varies 1.9×. It is now a fixed share of the node that earned it, which
  //     makes the tax identical for every weapon; see `ATTACKER_HITSTOP_FRAC`
  //     for the measurement that rules out the obvious constant-ms fix.
  //
  //     Neither must be scaled by damage or a big crit would freeze the game.
  //
  //     A continuous weapon is explicitly exempt from both. The drill
  //     re-contacts several times inside one active window, and full hitstop per
  //     contact turned its 168 ms grind into hit → freeze → hit → freeze at
  //     roughly a fifth of real time. Sustained contact is a damage channel, not
  //     an impact.
  if (!hit.continuous) {
    const targetStop = targetHitstopMs(wp.weight) / 1000;
    const attackerStop = attackerHitstopMs(wp, node) / 1000;
    out.hitstop = targetStop;
    w.hitstop[a] = Math.max(w.hitstop[a], attackerStop);
    w.hitstop[t] = Math.max(w.hitstop[t], targetStop);
  }

  // --- knockback
  const kbs = knockbackScale(wp.weight) * wp.knockbackMul * mods.knockbackMul;
  const massMul = 1 / Math.max(0.25, w.mass[t]);
  out.knockX = hit.dirX * wp.def.knockback.x * kbs * massMul * 0.5;
  out.knockY = wp.def.knockback.y * kbs * massMul * 0.5 + hit.dirY * 30 * kbs;
  if ((w.bodyFlags[t] & BodyFlag.NO_GRAVITY) === 0) {
    w.vx[t] += out.knockX;
    w.vy[t] += out.knockY;
  }

  // --- iframes
  if (!hit.continuous) w.iframes[t] = Math.max(w.iframes[t], iframeFor(wp.weight));

  // --- primers this weapon applies
  //
  // Only what the *node* declares, plus rolled module statuses. Applying every
  // entry of `wp.primes` on every hit — which is what this did — meant any
  // weapon that primes and detonates the same kind detonated itself on
  // alternate hits, for free, solo: measured at +122% on `sunder_gauntlet`.
  // The weapon-level `primes` array is a declaration for the synergy UI of §7,
  // and `ComboNode.primerApplied` is the runtime effect. That is also what
  // restores the dagger's "applies primers fastest" identity, since the dagger
  // is the weapon that declares a primer on every node of its chain.
  const primerDur = 3.0 + wp.weight * 2;
  if (node?.primerApplied !== undefined) {
    applyStatus(w, t, node.primerApplied, primerDur, 1);
  }
  if (hit.chargePrimer !== 0) applyStatus(w, t, hit.chargePrimer, primerDur, 1);
  for (const s of wp.statuses) applyStatusRolled(w, t, s, rng);

  // --- terrain: the weapon touches the world on its active frame
  applyTerrain(cells, wp.terrain, hit.hx, hit.hy, rng);

  // --- lifesteal and self-risk
  const steal = wp.lifestealFrac + mods.lifestealFrac;
  if (steal > 0) {
    out.lifesteal = dmg * steal;
    w.hp[a] = Math.min(w.maxHp[a], w.hp[a] + out.lifesteal);
  }
  out.selfDamage = applySelfRisk(w, hit, out, active | (out.crit ? TriggerFlag.CRIT : 0) |
    (out.detonated !== 0 ? TriggerFlag.DETONATE : 0));

  return out;
}

/** Damage a starving symbiote adds at full hunger. It gets sharper as it gets hungrier. */
const HUNGER_DAMAGE_FRAC = 0.45;
/** Damage a full battery adds when the heavy finisher spends it. */
const DISCHARGE_DAMAGE_FRAC = 0.9;

/**
 * What `WeaponInstance.charge` is worth to this particular blow.
 *
 * Two of the four charge identities pay out in damage. Fuel does not — an empty
 * drill simply stops, which is a harder consequence than a smaller number — and
 * spin already pays out through the charge tiers the state machine locked in at
 * release.
 *
 * The discharge is the runtime reader `ModuleEffect.melee.chargePerHit` never
 * had: galvanic's "charge builds on hits, discharge on heavy" (§5.2), where the
 * heavy finisher is the only node that can cash it.
 */
function chargePayout(wp: ResolvedWeapon, node: ComboNode | null): number {
  const charge = wp.instance.charge;
  if (charge <= 0) return 1;
  switch (chargeKindOf(wp.def)) {
    case 'hunger':
      return 1 + charge * HUNGER_DAMAGE_FRAC;
    case 'battery': {
      if (node?.entry !== 'heavy') return 1;
      wp.instance.charge = 0;
      return 1 + charge * DISCHARGE_DAMAGE_FRAC;
    }
    default:
      return 1;
  }
}

function applyStatusRolled(w: World, t: number, s: StatusApplication, rng: Rng): void {
  if (s.chance >= 1 || rng.chance(s.chance)) {
    applyStatus(w, t, s.kind, s.duration, s.stacks);
  }
}

/**
 * Baseline value of every stat. A body with nothing invested sits here.
 *
 * Scaling is measured as deviation from it rather than as an absolute, because
 * the coefficients on the 29 weapons run 0.2–1.4: read absolutely, an unspent
 * level-one character would already be swinging a greatblade at +120%. Read as
 * a deviation, the authored numbers say what they look like they say — "this
 * weapon pays out 1.2× per point of might" — and today's damage is unchanged.
 */
export const BASE_STAT = 1;

function statScaling(def: WeaponDef, stats: CombatStats): number {
  let s = 0;
  const sc = def.scaling;
  if (sc.might) s += sc.might * (stats.might - BASE_STAT);
  if (sc.finesse) s += sc.finesse * (stats.finesse - BASE_STAT);
  if (sc.focus) s += sc.focus * (stats.focus - BASE_STAT);
  if (sc.resonance) s += sc.resonance * (stats.resonance - BASE_STAT);
  if (sc.vitality) s += sc.vitality * (stats.vitality - BASE_STAT);
  if (sc.tech) s += sc.tech * (stats.tech - BASE_STAT);
  // A dumped stat may make a weapon bad; it may never make it heal the target.
  return Math.max(-0.9, s);
}

function anyPrimer(w: World, t: number): boolean {
  const base = t * World.STATUS_SLOTS;
  for (let s = 0; s < World.STATUS_SLOTS; s++) {
    const k = w.statusKind[base + s];
    if (k >= PrimerKind.BURNING && k <= PrimerKind.IRRADIATED && w.statusTime[base + s] > 0) {
      return true;
    }
  }
  return false;
}

/** Prefer a primer on the body; fall back to one lying on the terrain. */
function findPrimer(w: World, cells: CellWorld, hit: HitInput): PrimerKindId | 0 {
  for (const p of hit.weapon.detonates) {
    if (hasStatus(w, hit.target, p)) return p;
  }
  return findTerrainPrimer(
    cells,
    Math.floor(hit.hx),
    Math.floor(hit.hy),
    Math.max(2, hit.weapon.terrain.destroy.radius * 0.5),
    hit.weapon.detonates,
  );
}

/**
 * "The detonation is always more than the sum." Each primer converts into a
 * different *kind* of payload rather than a bigger number of the same kind,
 * which is what makes pairs feel authored instead of additive.
 */
function detonate(
  w: World,
  cells: CellWorld,
  hit: HitInput,
  primer: PrimerKindId,
  baseDamage: number,
  detonationMul: number,
  rng: Rng,
): number {
  const t = hit.target;
  const stacks = Math.max(1, statusStacks(w, t, primer));
  const amp = (0.65 + 0.22 * stacks) * detonationMul;
  let bonus = baseDamage * amp;
  const x = Math.floor(hit.hx);
  const y = Math.floor(hit.hy);

  switch (primer) {
    case PrimerKind.BURNING:
      cells.heatCircle(x, y, 9 + stacks * 2, 420);
      bonus *= 1.15;
      break;
    case PrimerKind.OILED:
      // Oil is the biggest detonation in the game because it is also the most
      // work to set up and the most dangerous to stand next to.
      cells.heatCircle(x, y, 12 + stacks * 3, 620);
      applyStatus(w, t, StatusKind.BURNING, 5, 2);
      bonus *= 1.45;
      break;
    case PrimerKind.FROZEN:
      // Shatter cone: the hammer's signature.
      cells.damageCircle(x, y, 8 + stacks * 2, 4);
      bonus *= 1.3;
      break;
    case PrimerKind.SHOCKED:
      chainShock(w, cells, hit, stacks);
      break;
    case PrimerKind.CORRODED:
      applyStatus(w, t, StatusKind.ARMOR_SHRED, 999, 2);
      cells.damageCircle(x, y, 5 + stacks, 3);
      break;
    case PrimerKind.BLEEDING:
      applyStatus(w, t, StatusKind.BLEEDING, 6, 2);
      bonus *= 1.2;
      break;
    case PrimerKind.INFECTED:
      applyStatus(w, t, StatusKind.SPORED, 6, 2);
      spray(cells, x, y, 14, rng);
      break;
    case PrimerKind.CURSED:
      // Lifesteal burst: the curse dagger + blood scythe pair from §1.
      w.hp[hit.attacker] = Math.min(w.maxHp[hit.attacker], w.hp[hit.attacker] + bonus * 0.35);
      break;
    case PrimerKind.MARKED:
      bonus *= 1.35;
      break;
    case PrimerKind.PHASED:
      // Phased targets have nothing anchoring them; the blow lands everywhere.
      bonus *= 1.25;
      applyStatus(w, t, StatusKind.STAGGERED, 1.0, 1);
      break;
    case PrimerKind.STAGGERED:
      bonus *= 1.4;
      break;
    case PrimerKind.IRRADIATED:
      applyStatus(w, t, StatusKind.WEAKENED, 5, 1);
      bonus *= 1.15;
      break;
    default:
      break;
  }

  removeStatus(w, t, primer);
  return bonus;
}

function chainShock(w: World, cells: CellWorld, hit: HitInput, stacks: number): void {
  const r = 40 + stacks * 12;
  const r2 = r * r;
  const sx = hit.hx;
  const sy = hit.hy;
  let chained = 0;
  w.each(Comp.HEALTH, (j) => {
    if (chained >= 3 || j === hit.target || j === hit.attacker) return;
    if (w.team[j] === w.team[hit.attacker]) return;
    const dx = w.x[j] - sx;
    const dy = w.y[j] - sy;
    if (dx * dx + dy * dy > r2) return;
    applyStatus(w, j, StatusKind.SHOCKED, 2.5, 1);
    chained++;
  });
  cells.heatCircle(Math.floor(sx), Math.floor(sy), 4, 40);
}

function spray(cells: CellWorld, x: number, y: number, n: number, rng: Rng): void {
  for (let k = 0; k < n; k++) {
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(0, 8);
    const px = Math.round(x + Math.cos(a) * d);
    const py = Math.round(y + Math.sin(a) * d);
    if (cells.peek(px, py) === VOID) cells.set(px, py, MAT.SPORE_CLOUD);
  }
}

/**
 * Apply a weapon's terrain interaction at a point. Every weapon has one — §9 is
 * explicit that no class may ignore terrain — so this runs on every active
 * frame, not only on hit.
 *
 * **The edge is per cell, not per swing (#132).** `d.maxHardness` is the force
 * the blow brings; `digCell` multiplies it by the routes that cell offers —
 * the damage type it yields to, and whether something already weakened it. Both
 * destruction branches go through the one helper so the two cannot drift, which
 * is the shape the `chance >= 1` branch used to break by calling `damageCircle`
 * with the raw number.
 */
export function applyTerrain(
  cells: CellWorld,
  t: TerrainInteraction,
  wx: number,
  wy: number,
  rng: Rng,
): void {
  const x = Math.floor(wx);
  const y = Math.floor(wy);
  const d = t.destroy;

  if (d.radius > 0 && d.maxHardness > 0) {
    if (d.chance >= 1) {
      const r = Math.floor(d.radius);
      const r2 = d.radius * d.radius;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r2) continue;
          digCell(cells, x + dx, y + dy, d.maxHardness, t.damageType);
        }
      }
    } else if (d.chance > 0) {
      const r = Math.ceil(d.radius);
      const r2 = d.radius * d.radius;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r2) continue;
          if (!rng.chance(d.chance)) continue;
          if (t.displaces) displaceCell(cells, x + dx, y + dy, dx, dy);
          else digCell(cells, x + dx, y + dy, d.maxHardness, t.damageType);
        }
      }
    }
  }

  if (t.heat) cells.heatCircle(x, y, Math.max(2, d.radius), t.heat);

  if (t.charge) {
    const r = Math.max(2, Math.ceil(d.radius));
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const m = cells.peek(x + dx, y + dy);
        if (m === VOID) continue;
        if (M.electricConductivity[m] > 0.1 || (cells.getFlags(x + dx, y + dy) & Flag.WET)) {
          cells.addFlag(x + dx, y + dy, Flag.CHARGED);
        }
      }
    }
  }

  if (t.corrode) {
    const r = Math.max(1, Math.ceil(d.radius * 0.6));
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const m = cells.peek(x + dx, y + dy);
        if (m === VOID) continue;
        if (rng.chance(t.corrode * (1 - M.corrosionResist[m]) * 0.5)) {
          cells.addFlag(x + dx, y + dy, Flag.CORRODING);
        }
      }
    }
  }

  if (t.emit && t.emit.count > 0) {
    for (let k = 0; k < t.emit.count; k++) {
      const a = rng.range(0, Math.PI * 2);
      const dist = rng.range(0, Math.max(2, d.radius));
      const px = Math.round(x + Math.cos(a) * dist);
      const py = Math.round(y + Math.sin(a) * dist);
      if (cells.peek(px, py) === VOID) cells.set(px, py, t.emit.mat);
    }
  }
}

/** Shove a loose cell one step outward instead of deleting it. */
function displaceCell(cells: CellWorld, x: number, y: number, dx: number, dy: number): void {
  const m = cells.peek(x, y);
  if (m === VOID || M.indestructible[m] === 1) return;
  if (M.mobile[m] !== 1) {
    cells.damageCell(x, y, 3);
    return;
  }
  const nx = x + Math.sign(dx);
  const ny = y + Math.sign(dy);
  if (cells.peek(nx, ny) === VOID) cells.swap(x, y, nx, ny);
}

/** Self-risk resolution (§5.3). Returns damage dealt to the attacker. */
function applySelfRisk(
  w: World,
  hit: HitInput,
  out: HitResult,
  active: TriggerMask,
): number {
  let self = 0;
  const a = hit.attacker;
  const masks = hit.weapon.riskMasks;
  for (let k = 0; k < hit.weapon.risks.length; k++) {
    const gate = masks[k] ?? 0;
    // A gated risk bills only while its module is doing something.
    if (gate !== 0 && (gate & active) === 0) continue;
    const risk = hit.weapon.risks[k];
    switch (risk.kind) {
      case 'selfDamage':
        self += risk.magnitude;
        break;
      case 'healthCost':
        self += w.maxHp[a] * risk.magnitude;
        break;
      case 'selfStatus':
        if (risk.status !== undefined) applyStatus(w, a, risk.status, 4, 1);
        break;
      case 'overheat':
        hit.weapon.instance.heat = Math.min(1, hit.weapon.instance.heat + risk.magnitude);
        if (hit.weapon.instance.heat >= 1) applyStatus(w, a, StatusKind.OVERHEATED, 3, 1);
        break;
      case 'friendlyFire':
        // The blast is authored to reach past the wielder's own reach; that is
        // the point of the module, so the damage is not optional.
        if (out.detonated !== 0) self += out.damage * risk.magnitude * 0.25;
        break;
      default:
        break;
    }
  }
  if (self > 0) w.hp[a] -= self;
  return self;
}
